// src/pages/Login.tsx
import React, { useState } from "react";
import { useLoginMutation } from "../services/auth";
import { useDispatch } from "react-redux";
import { setCredentials } from "../slices/authSlice";
import { Link, useNavigate } from "react-router-dom";

export default function LoginPage() {
  const navigate = useNavigate();
  const [login] = useLoginMutation();
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // server expects deviceInfo for session tracking
      const res: any = await login({
        email,
        password,
        deviceInfo: navigator.userAgent,
      }).unwrap();

      // response should include token, email, role, firstName, lastName
      const { token, email: respEmail, role, firstName, lastName } = res;

      // persist credentials including names
      dispatch(
        setCredentials({
          token,
          email: respEmail,
          role,
          firstName,
          lastName,
        })
      );

      // route based on role
      if (role === "admin") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err: any) {
      console.error("Login error", err);
      // RTK Query errors often live under err.data or err.error
      const msg =
        err?.data?.error ||
        err?.data?.message ||
        err?.error ||
        err?.message ||
        "Login failed. Check your credentials.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <form
        onSubmit={onSubmit}
        className="bg-white p-8 rounded shadow w-full max-w-md"
        aria-busy={loading}
      >
        <h2 className="text-2xl mb-4">Login</h2>

        {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

        <label className="block mb-2 text-sm">
          <span className="text-slate-700">Email</span>
          <input
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mt-1 p-2 border rounded"
            placeholder="you@example.com"
            type="email"
            disabled={loading}
          />
        </label>

        <label className="block mb-3 text-sm">
          <span className="text-slate-700">Password</span>
          <input
            required
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mt-1 p-2 border rounded"
            placeholder="••••••••"
            disabled={loading}
          />
        </label>

        <div className="flex items-center justify-between mb-4">
          <Link
            to="/forgot-password"
            className="text-sm text-blue-600 hover:underline"
          >
            Forgot password?
          </Link>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-60"
            disabled={loading}
          >
            {loading ? "Signing in..." : "Login"}
          </button>
        </div>
      </form>
    </div>
  );
}
